/**
 * The "Shortcut key" field for an action button (and a Line Entry command):
 * the designer PRESSES the keys and the box records them ("Ctrl+Shift+A").
 *
 * A system key (F3 Add, F4 Edit, Ctrl+S Save, F10 Save and close …) or one
 * the browser owns is refused on the spot with the reason, and the previous
 * key stays — the rules are `txn/actionShortcuts.json`, the same file the
 * lint, the save and the runtime read. `clash` is the parent's own message
 * (the key is already another button's). Backspace / Delete clears; Tab
 * moves on; Esc leaves the field.
 *
 * Shared by the transaction designer's action panel, Make changes' button
 * panel and the Line Entry command editor.
 */
import { useState, type CSSProperties } from 'react'

import { displayCombo, displayForEvent, parseShortcut, systemShortcuts, type ShortcutParse } from '../txn/actionShortcuts'
import { tw } from '../testHooks'

export interface ShortcutKeyInputProps {
  value?: string
  onChange: (v: string | undefined) => void
  /** A problem the parent found (a key another button already uses). */
  clash?: string
  /** Style of the input (the host's own field style). */
  inputStyle?: CSSProperties
  /** Under the field: which keys are the system's (default on). */
  showSystemKeys?: boolean
  /** Test hook (`data-tw-field`). */
  hook?: string
  disabled?: boolean
}

const errStyle: CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-error, #cf222e)', marginTop: 3 }
const helpStyle: CSSProperties = { fontSize: '0.75rem', color: 'var(--color-text-secondary, #465062)', marginTop: 3 }

export default function ShortcutKeyInput({
  value, onChange, clash, inputStyle, showSystemKeys = true, hook = 'action-shortcut', disabled,
}: ShortcutKeyInputProps) {
  const [refused, setRefused] = useState<string | null>(null)
  const parsed: ShortcutParse = parseShortcut(value)
  // A stored key that is not usable (hand-edited, or written before the rule)
  // shows its problem so the designer can replace it.
  const stored = value && parsed.problem ? parsed.problem.message : null
  const message = refused || stored || clash || null

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const ne = e.nativeEvent
    const plain = !ne.ctrlKey && !ne.metaKey && !ne.altKey && !ne.shiftKey
    if (plain && e.key === 'Tab') return
    e.preventDefault()
    e.stopPropagation()
    if (plain && e.key === 'Escape') { (e.target as HTMLInputElement).blur(); return }
    if (plain && (e.key === 'Backspace' || e.key === 'Delete')) {
      setRefused(null)
      onChange(undefined)
      return
    }
    const text = displayForEvent(ne)
    if (!text) return  // only a modifier so far
    const p = parseShortcut(text)
    if (p.problem || !p.combo) {
      setRefused(p.problem?.message || `${text} cannot be used.`)
      return
    }
    setRefused(null)
    onChange(p.display)
  }

  const sys = showSystemKeys ? systemShortcuts().map(s => `${displayCombo(s.combo)} ${s.label}`) : []

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          type="text"
          readOnly
          disabled={disabled}
          value={value ? (parsed.display || value) : ''}
          placeholder="Press the keys, e.g. Alt+A or F7"
          aria-invalid={!!message}
          data-tw-field={hook}
          onKeyDown={onKeyDown}
          onBlur={() => setRefused(null)}
          style={{
            ...inputStyle,
            flex: 1,
            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
            cursor: 'text',
            ...(message ? { border: '1px solid var(--color-error, #ef5350)' } : {}),
          }}
        />
        {value && !disabled && (
          <button type="button" onClick={() => { setRefused(null); onChange(undefined) }}
            {...tw('action', `${hook}-clear`)}
            title="Remove the shortcut"
            style={{ border: '1px solid var(--color-border, #dde3ea)', background: 'transparent',
              borderRadius: 4, padding: '2px 8px', fontSize: '0.8125rem', cursor: 'pointer',
              color: 'var(--color-text-secondary, #465062)' }}>
            Clear
          </button>
        )}
      </div>
      {message && <div style={errStyle} role="alert">{message}</div>}
      {showSystemKeys && (
        <div style={helpStyle}>System keys (cannot be used): {sys.join(' · ')}</div>
      )}
    </div>
  )
}

